<script setup>
// Single owner of the card-dashboard result-handling logic (URL-scheme
// allowlist, popup-blocked fallback, N-result picker markup). Both
// CardSearchButton.vue (the inline per-field search) and
// CardSearchComboButton.vue (the combined search, COMBO-01..09) delegate here
// through the exposed `present(list)` method, so a future fix to the
// URL-scheme check or the popup-blocked heuristic lands once, not twice.
// Origem: ADR 0008 (v0.3.1), extracted this phase (Phase 9).
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();

const results = ref([]);
const dialogRef = ref(null);

const openPicker = list => {
  results.value = list;
  dialogRef.value?.open();
};

// Results come from the internal card-dashboard search API, but the URL is
// opened with the dashboard's own origin — validate the scheme so a
// compromised/misconfigured backend can't execute a `javascript:`/`data:`
// URI here.
const isSafeUrl = url => {
  try {
    return ['http:', 'https:'].includes(new URL(url).protocol);
  } catch {
    return false;
  }
};

// Takes a non-empty result array and decides how to present it: a single
// result auto-opens in a new tab (falling back to the picker when the popup
// is blocked), more than one result always opens the picker. Deciding what
// an empty result set means (immediate not_found vs. falling through to the
// next cascade step) belongs to the caller, so an empty list here indicates
// a caller bug rather than a valid "no results" state.
const present = list => {
  if (list.length === 0) {
    throw new Error(
      'CardSearchResultDialog.present() requires a non-empty list'
    );
  }
  if (list.length === 1) {
    if (!isSafeUrl(list[0].url)) {
      useAlert(t('CDC.CARD_SEARCH.toast.error'));
      return;
    }
    // No windowFeatures argument here: `noopener`/`noreferrer` force
    // window.open() to return null unconditionally, which would make the
    // blocked-detection below permanently true and delete the auto-open
    // path entirely. The reverse-tabnabbing link is severed explicitly
    // below instead, once we know the popup was not blocked.
    const popup = window.open(list[0].url, '_blank');
    const wasBlocked =
      !popup || popup.closed || typeof popup.closed === 'undefined';
    if (wasBlocked) {
      openPicker(list);
    } else {
      popup.opener = null;
    }
  } else {
    openPicker(list);
  }
};

const onPickResult = result => {
  if (!isSafeUrl(result.url)) {
    useAlert(t('CDC.CARD_SEARCH.toast.error'));
    return;
  }
  dialogRef.value?.close();
  window.open(result.url, '_blank', 'noopener,noreferrer');
};

defineExpose({ present });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CDC.CARD_SEARCH.modal.title')"
    show-cancel-button
    :show-confirm-button="false"
    width="md"
    overflow-y-auto
  >
    <div class="flex flex-col gap-2 max-h-80 overflow-y-auto">
      <button
        v-for="result in results"
        :key="result.url"
        type="button"
        class="flex items-center justify-between w-full px-4 py-2 text-left rounded-lg hover:bg-n-alpha-2"
        @click="onPickResult(result)"
      >
        <div class="flex flex-col overflow-hidden">
          <span
            class="overflow-hidden text-sm text-n-slate-12 whitespace-nowrap text-ellipsis"
          >
            {{ result.name }}
          </span>
          <span class="text-xs text-n-slate-10">{{ result.document }}</span>
        </div>
        <div class="flex items-center gap-1 shrink-0">
          <span
            v-if="result.inactive"
            class="rounded-full bg-n-alpha-2 px-1.5 text-xs text-n-slate-11"
          >
            {{ t('CDC.CARD_SEARCH.modal.inactive_badge') }}
          </span>
          <span
            v-if="result.kind === 'dependant'"
            class="rounded-full bg-n-alpha-2 px-1.5 text-xs text-n-slate-11"
          >
            {{ t('CDC.CARD_SEARCH.modal.dependant_badge') }}
          </span>
        </div>
      </button>
    </div>
  </Dialog>
</template>
